import { useRef, useEffect, useCallback } from "react";

const SCROLL_HIDE_DELAY = 900;

/**
 * Callback ref for scrollable regions with slim scrollbars that reveal on hover/scroll.
 */
const useSlimScroll = (active = true) => {
  const hideTimerRef = useRef(null);
  const scrollRef = useRef(null);

  const revealScrollbar = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.classList.add("is-scrolling");
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      el.classList.remove("is-scrolling");
    }, SCROLL_HIDE_DELAY);
  }, []);

  const setScrollRef = useCallback(
    (node) => {
      if (scrollRef.current) {
        scrollRef.current.removeEventListener("scroll", revealScrollbar);
      }

      scrollRef.current = node;

      if (node && active) {
        node.addEventListener("scroll", revealScrollbar, { passive: true });
      }
    },
    [active, revealScrollbar]
  );

  useEffect(() => {
    if (!active && scrollRef.current) {
      scrollRef.current.removeEventListener("scroll", revealScrollbar);
      scrollRef.current.classList.remove("is-scrolling");
    }
  }, [active, revealScrollbar]);

  useEffect(
    () => () => {
      clearTimeout(hideTimerRef.current);
      if (scrollRef.current) {
        scrollRef.current.removeEventListener("scroll", revealScrollbar);
        scrollRef.current.classList.remove("is-scrolling");
      }
    },
    [revealScrollbar]
  );

  return setScrollRef;
};

export default useSlimScroll;
